import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  CellClickedEvent,
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  GetContextMenuItemsParams,
  GridReadyEvent,
  ICellRendererParams,
  MenuItemDef,
} from 'ag-grid-community';
import { debounceTime, forkJoin } from 'rxjs';
import { AGTableBase } from '../../../shared/components/table/ag-table-base';
import { TestDetails } from '../../../shared/models/test-result.model';
import { AuthUser, Role } from '../../../shared/models/user.model';
import { VoiceTestResult, VoiceTestResultCollection } from '../../../shared/models/voice-testing.model';
import { BrowserUtils } from '../../../shared/services/browser-utils';
import { CustomUtils } from '../../../shared/services/custom-utils';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { AllRequestParams } from '../../../shared/services/manual-number-testing.service';
import { DialogRef, ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { ReimburseUtils } from '../../../shared/services/reimburse-utils';
import { UsersService } from '../../../shared/services/users.service';
import { SearchParams, VoiceTestingService } from '../../../shared/services/voice-testing.service';
import { SipResultsTableActionsComponent } from './sip-results-table-actions/sip-results-table-actions.component';
import { SipResultsTableStatusComponent } from './sip-results-table-status/sip-results-table-status.component';
declare const moment: any;

@Component({
  standalone: false,
  selector: 'app-sip-results-table',
  templateUrl: 'sip-results-table.component.html',
  styleUrls: ['sip-results-table.component.scss'],
})
export class SipResultsTableComponent extends AGTableBase implements OnInit, OnDestroy {
  private readonly PREFIX = 'sip_results_table_';

  @ViewChild('copyModalTpl', { read: TemplateRef, static: false }) copyModalTpl: any;
  @ViewChild('cliModalTpl', { read: TemplateRef, static: false }) cliModalTpl: any;
  @ViewChild('commentModalTpl', { read: TemplateRef, static: false }) commentModalTpl: any;
  @ViewChild('reimburseModalTpl', { read: TemplateRef, static: false }) reimburseModalTpl: any;

  @Output() actions = new EventEmitter<unknown>();
  @Output() onAfterDelete = new EventEmitter<unknown>();
  @Output() onRepeat = new EventEmitter<unknown>();
  @Output() changeBatch = new EventEmitter<VoiceTestResult[]>();

  isAdmin = false;
  user: AuthUser;
  loading = false;

  commentModel;
  commentModal: DialogRef;

  currentSection = '';
  intervalTimer: any;
  batchSelectedIds = new Set<number>();

  searchParams: SearchParams = {
    testGroupIds: [],
    testIds: [],
    userIds: [],
    networkDetails: [],
    callerIds: [],
    statuses: [],
    sip: true,
  };
  taskId: any;
  textModalHtml: string;
  callerDifference: any;
  testCaseDetails: TestDetails[] = [];

  // ng grid
  columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'batch',
      checkboxSelection: true,
      maxWidth: 40,
      headerCheckboxSelection: true,
      pinned: 'left',
      lockPinned: true,
      lockPosition: 'left',
      lockVisible: true,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      suppressMovable: true,
    },
    {
      headerName: 'ID',
      field: 'id',
      hide: true,
      flex: 0.5,
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${_.id}</div>`;
      },
    },
    {
      headerName: 'Date',
      field: 'createdAtFormat',
      minWidth: 90,
      maxWidth: 90,
      flex: 0.6,
      headerTooltip: 'Date and time when the test was triggered, in the browser’s time zone',
      cellRenderer: (params: ICellRendererParams<VoiceTestResult>) => {
        const _ = params.data;
        return this.formatDate(_);
      },
    },
    {
      headerName: 'UTC Date',
      field: 'utcTimestamp',
      minWidth: 90,
      maxWidth: 90,
      flex: 0.6,
      headerTooltip: 'UTC date for test created date.',
      hide: true,
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return CustomUtils.formatDateIntoUTC(_.createdAt);
      },
    },
    {
      headerName: 'Country(MCC)/Network(MNC)',
      field: 'destination',
      headerTooltip: 'The destination to which the test was sent',
      flex: 1.5,
      cellRenderer: (params: ICellRendererParams<VoiceTestResult>) => {
        return `<div class="two-lines">${params.value ? this.formatCountry(params.value) : ''}</div>`;
      },
    },
    {
      headerName: 'SIP/Rec. Status',
      field: 'testStatus',
      headerTooltip: 'Receipt status of the test, indicating whether the test was delivered to our test number',
      cellClass: 'd-flex align-items-center justify-content-center',
      minWidth: 90,
      flex: 0.4,
      cellRenderer: SipResultsTableStatusComponent,
    },
    {
      headerName: 'CLI',
      field: 'callerId',
      headerTooltip: 'Calling Line Identification',
      tooltipField: 'callerId',
      minWidth: 130,
      flex: 1,
      cellRenderer: (params: ICellRendererParams<VoiceTestResult>) => {
        const row = params.data;
        return this.formatCli(row);
      },
      onCellClicked: (params: CellClickedEvent<VoiceTestResult>) => this.onCellClick(params),
    },
    {
      headerName: 'SIP CLI',
      field: 'sipCallerId',
      headerTooltip: 'Calling Line Identification',
      minWidth: 130,
      flex: 1,
      initialHide: true,
      cellRenderer: (params: ICellRendererParams<VoiceTestResult>) => {
        const row = params.data;
        return '<div class="one-line">' + (row.sipCallerId ? row.sipCallerId : 'N/A') + '</div>';
      },
    },
    {
      headerName: 'Phone',
      field: 'destination.phone',
      headerTooltip: 'Test number assigned to this test',
      flex: 1,
      minWidth: 130,
      cellRenderer: (params: ICellRendererParams<VoiceTestResult>) => {
        const row = params.data;
        if (row.destination && row.destination.phone) {
          return row.destination.phone;
        }
        return 'N/A';
      },
    },
    {
      headerName: 'Delay',
      field: 'delay',
      headerTooltip: 'The time interval between the creation of a test and the delivery of a test to our server',
      minWidth: 90,
      maxWidth: 90,
      flex: 0.6,
      cellRenderer: (params: ICellRendererParams<VoiceTestResult>) => {
        const row = params.data;
        return `<div class="two-lines clickable">${this.formatDelay(row)}</div>`;
      },
    },
    {
      headerName: 'User',
      field: 'user',
      flex: 0.5,
      headerTooltip: 'User that triggered the test',
      cellRenderer: (params: ICellRendererParams<VoiceTestResult>) => {
        const _ = params.data;
        return `<div class="one-line">${_.user ? (_.user.username ? _.user.username : _.user.email) : 'N/A'}<div>`;
      },
    },
    {
      headerName: 'Price',
      field: 'price',
      hide: true,
      flex: 0.4,
      cellRenderer: (params: ICellRendererParams<VoiceTestResult>) => {
        const _ = params.data;
        return _.price > 0 ? _.price / 1000 : 0;
      },
    },
    {
      headerName: 'SIP Trunk',
      field: 'sipConfig.name',
      headerTooltip: 'Name of the SIP configuration used for the test',
      flex: 1,
      cellRenderer: (params: ICellRendererParams<VoiceTestResult>) => {
        const sipConfig = params.data?.sipConfig;
        return sipConfig ? sipConfig.name : 'N/A';
      },
    },
    {
      headerName: 'Actions',
      field: 'actions',
      maxWidth: 100,
      minWidth: 100,
      pinned: 'right',
      lockPinned: true,
      lockPosition: 'right',
      lockVisible: true,
      suppressColumnsToolPanel: false,
      suppressMenu: false,
      suppressAutoSize: true,
      tooltipField: 'actions',
      headerTooltip: 'Right click on a row to see more options.',
      headerClass: 'action-cell',
      cellClass: 'action-cell',
      cellRenderer: SipResultsTableActionsComponent,
      onCellClicked: (event: CellClickedEvent) => this.onActionClick(event),
    },
  ];

  rowData!: VoiceTestResult[];
  // ng grid

  constructor(
    public router: Router,
    public notificationService: NotificationService,
    public service: VoiceTestingService,
    public userService: UsersService,
    public localStorage: LocalStorageService,
    private clipboard: Clipboard,
    public modal: ModalService
  ) {
    super();
  }

  ngOnInit() {
    this.userService
      .getAuthUser()
      .then(user => {
        this.isAdmin = user.role === Role.ADMIN;
        this.user = user;
        this.init();
      })
      .catch(e => {});
    this.actionSubscription = this.service.action$.subscribe(action => this.onAction(action));
    this.columnChange$.pipe(debounceTime(1000)).subscribe((event: ColumnMovedEvent | ColumnResizedEvent | ColumnVisibleEvent) => {
      this.localStorage.set(`${this.PREFIX}state_${this.user.id}`, this.gridApi.getColumnState());
    });
  }

  init() {
    const hideColumns = [];
    if (this.user.role === Role.SUB && !this.user.showAllResults) {
      hideColumns.push('user');
    }
    this.columnDefs = this.columnDefs.filter(c => hideColumns.indexOf(c.field) === -1);
    this.paginationPageSize = this.localStorage.get(`${this.PREFIX}size_${this.user.id}`) ?? 20;
    const savedUserSearchDetails = this.localStorage.get(`sip_user_search_${this.user.id}_search`);
    if (savedUserSearchDetails) {
      this.searchParams = savedUserSearchDetails;
    }
    this.intervalTimer = setInterval(() => {
      if (this.loading) {
        return;
      }
      this.update(false);
    }, 1000 * 20);
  }

  formatDate(_: VoiceTestResult) {
    return `<div class="two-lines"><span>${_.createdAtFormat[1]} <br>${_.createdAtFormat[0]}</span></div>`;
  }

  formatCountry(c, icon = true) {
    return this.service.formatDestination(c, icon);
  }

  formatDelay(row: VoiceTestResult) {
    const delay = row.delay;
    const toSec = del => {
      const hours = Math.floor(del / 3600);
      const minutes = Math.floor((del - hours * 3600) / 60);
      const seconds = del - hours * 3600 - minutes * 60;

      const parts = [];
      if (hours) {
        parts.push(hours + 'h');
      }
      if (minutes) {
        parts.push(minutes + 'm');
      }
      if (seconds || !parts.length) {
        parts.push(seconds + 's');
      }

      const text = parts.join(' ');
      return del >= 300 ? `<span class="text-accent-a2">${text}</span>` : text;
    };

    if (typeof delay === 'number') {
      return toSec(delay);
    }
    return 'N/A';
  }

  changeSize($event, size) {
    this.paginationPageSize = size;
    this.localStorage.set(`${this.PREFIX}size_${this.user.id}`, size);
    this.update();
  }

  update(loading = true) {
    if (!this.user.enabled) {
      return;
    }
    this.loading = loading;
    const params = new AllRequestParams();
    params.size = this.paginationPageSize;
    params.page = this.currentPageNumber - 1;
    const success = (data: VoiceTestResultCollection) => {
      data.content.map(_ => {
        const time = Date.parse(_.createdAt);
        _.createdAtFormat = moment(time).format('DD/MM/YY HH:mm:ss').split(' ');
        return _;
      });
      this.rowData = data.content;
      this.totalRowsCount = data.totalElements;
      this.loading = false;
      setTimeout(() => this.selectBatchNodes(), 0);
    };
    const error = error => {
      this.loading = false;
      this.rowData = [];
      this.totalRowsCount = 0;
      this.notificationService.error({
        title: 'SIP CLI testing',
        message: 'An error occurred while loading the results',
        serviceName: 'VTC',
        requestMessage: error.statusText,
        requestCode: error.status,
        ts: error.timestamp ? error.timestamp : null,
      });
    };

    this.service.results(params, this.searchParams).subscribe(success, error);
  }

  onAction(event) {
    this.actions.emit(event);

    if (event.name === 'reimburse') {
      return this.reimburseTest(event.row);
    }

    if (event.name !== 'copy') {
      return;
    }

    const row = event.row;
    this.testCaseDetails = [];
    this.modal
      .alert()
      .component(this.copyModalTpl)
      .open()
      .onDestroy.subscribe(() => {});

    forkJoin([this.service.getResultDetailsText(row), this.service.getSipStatuses(row.id)]).subscribe({
      next: ([details, statuses]) => {
        const ordered = statuses.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));

        const statusData = ordered.map(s => {
          const ts = moment(s.createdAt).format('DD/MM/YY HH:mm:ss');
          return `${ts}: ${s.sipStatusCode} - ${s.sipStatusReason}`;
        });

        details.push({
          id: 'sip_statuses',
          title: 'SIP Statuses',
          data: statusData,
        });

        this.testCaseDetails = this.getFormattedData(details);
        this.currentSection = this.testCaseDetails[0].id;
      },
      error: e => console.error(e),
    });
  }

  onCellClick(event: CellClickedEvent) {
    switch (event.column.getColId()) {
      case 'callerId':
        this.onCallerIdCellClick(event);
        break;
    }
  }

  onCallerIdCellClick(event: CellClickedEvent) {
    const dialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.cliModalTpl);
    const row = event.data as VoiceTestResult;
    const [a, b] = this.prepareCallerId(row.sipCallerId, row.callerId);
    const className = a !== b ? 'text-error' : 'text-sucess';
    this.callerDifference = {
      title1: 'Caller ID sent',
      value1: `${BrowserUtils.sanitizeHTML(row.sipCallerId)}`,
      title2: 'Caller ID delivered',
      value2: `<span class="${className}">${BrowserUtils.sanitizeHTML(row.callerId)}</span>`,
    };
    dialogRef.open().result.then(result => {
      this.callerDifference = null;
      if (result === 'viewSummary') {
        this.service.announceAction({ name: 'copy', row, column: 'action' });
      }
    });
  }

  getFormattedData(details: TestDetails[]): TestDetails[] {
    details.forEach(section => {
      section.data = section.data.map(detail => {
        const idx = detail.indexOf(': ');
        if (idx === -1) {
          return [detail, ''];
        }
        return [detail.slice(0, idx), detail.slice(idx + 2)];
      });
    });
    return details;
  }

  formatCli(row: VoiceTestResult) {
    let className = '';
    if (row.callerId) {
      const [a, b] = this.prepareCallerId(row.sipCallerId, row.callerId);
      className = a !== b ? 'text-error-r3' : 'text-success-s2';
    }
    const text = BrowserUtils.escapeHtml(row.callerId ?? row.sipCallerId);
    return `<div class="one-line clickable"><span class="${className}">${text}</span></div>`;
  }

  private prepareCallerId(sipCallerId: string, callerId: string): string[] {
    return [sipCallerId.startsWith('+') ? sipCallerId.slice(1) : sipCallerId, callerId.startsWith('+') ? callerId.slice(1) : callerId];
  }

  onSectionChange(sectionId: string) {
    this.currentSection = sectionId ? sectionId : this.testCaseDetails[0].id;
  }

  scrollTo(section) {
    document.querySelector('#' + section).scrollIntoView();
    setTimeout(() => {
      // Ensure nav item highlights even when scroll event doesn't fire
      this.onSectionChange(section);
    }, 0);
  }

  // ng grid
  getContextMenuItems = (params: GetContextMenuItemsParams<VoiceTestResult>): MenuItemDef[] => {
    const currentRowClicked = params.node.data;
    const result: MenuItemDef[] = [
      {
        name: 'Details ',
        icon: '<span class="icon-tests-settings fs-4"></span>',
        action: () => {
          this.service.announceAction({ name: 'copy', row: currentRowClicked, column: 'action' });
        },
      },
      {
        name: 'Copy',
        icon: '<span class="icon-copy fs-4"></span>',
        action: () => {
          const renderedValue = this.getRenderedCellValue(params);
          this.clipboard.copy(renderedValue ? renderedValue : currentRowClicked.id.toString());
        },
      },
      {
        name: 'Reimburse test',
        icon: '<span class="far fa-money-bill-alt ms-0_2 fs-4"></span>',
        disabled: !ReimburseUtils.canReimburse(currentRowClicked),
        action: () => {
          this.onAction({ name: 'reimburse', row: currentRowClicked, column: 'action' });
        },
      },
    ];

    if (this.isAdmin) {
      return result;
    }

    const adminActions = ['Reimburse test', 'Show modem'];

    return result.filter(item => !adminActions.includes(item.name));
  };

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.update();
    const columnState = this.localStorage.get(`${this.PREFIX}state_${this.user.id}`);
    this.gridApi.applyColumnState({ state: columnState });
  }

  onRowSelected() {
    const selectedRows = this.gridApi.getSelectedRows() as VoiceTestResult[];
    this.batchSelectedIds = new Set(selectedRows.map(row => row.id));
    this.changeBatch.emit(selectedRows);
  }

  resetBatch() {
    this.batchSelectedIds.clear();
    this.gridApi.deselectAll();
  }

  private selectBatchNodes() {
    this.gridApi.forEachNode(node => {
      if (this.batchSelectedIds.has(node.data.id)) {
        node.setSelected(true);
      }
    });
  }
  // ng grid

  ngOnDestroy() {
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer);
    }
    this.actionSubscription.unsubscribe();
    this.columnChange$.unsubscribe();
  }

  reimburseTest(row: VoiceTestResult): void {
    const dialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.reimburseModalTpl).open();
    dialogRef.result.then(result => {
      if (result) {
        this.loading = true;
        this.service.reimburse([row.id]).subscribe({
          next: () => {
            this.notificationService.success('Test reimbursed', 'Voice Testing');
            this.update();
          },
          error: e => {
            this.notificationService.error({
              title: 'Reimburse test',
              message: 'An error occurred while reimbursing the test',
              serviceName: 'VTC',
              requestMessage: e.statusText,
              requestCode: e.status,
              ts: e.timestamp ? e.timestamp : null,
            });
            this.loading = false;
          },
        });
      } else {
        this.notificationService.info('Reimbursement canceled', 'Voice Testing');
      }
    });
  }
}
